import { Avatar } from '@nextui-org/react';
import { Divider as NextDivider } from '@nextui-org/react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import { BellIcon, Chevron2Icon, VinCarLogoMark, VinCarLogoTypeColor } from '../../../assets';
import ZIndexLayers from '../../../services/ZIndexLayers';
import { ThemeProp } from '../../../theme/Themes';
import { SecondaryButton } from '../..';
import IconButton from '../../Button/IconButton';

export const headerHeight = '3.25rem';
export const twoLevelHeaderHeight = '6.125rem';
export const desktopheaderHeight = '4rem';

export const Container = styled.header<{ $mobilePadding?: string }>`
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface.default};
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  min-height: ${headerHeight};
  justify-content: space-between;
  left: 0;
  padding: 1rem 4.5rem 1rem 2.5rem;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${ZIndexLayers.navbar};
  vertical-align: middle;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${(props: ThemeProp) => props.theme.colors.layout.divider};
  gap: 0.75rem;

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.minMobile}) {
    position: sticky;
    flex-direction: row;
    height: ${desktopheaderHeight};
    padding: 0 2.5rem;
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    padding: ${(props) => props.$mobilePadding || '1rem'};
  }
`;

export const Line = styled(NextDivider)`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  background: ${(props: ThemeProp) => props.theme.colors.secondary['12-light-blue']};
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  vertical-align: middle;
  align-self: flex-start;

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.minMobile}) {
    height: 100%;
  }
`;

export const LeftContainer = styled(ContentContainer)`
  align-self: flex-start;
  align-items: center;
  flex: 1;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;
export const LeftContainerMobile = styled(ContentContainer)`
  align-self: flex-start;
  align-items: center;
  flex: 1;
  width: 100%;
  display: none;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;

export const RightContainer = styled(LeftContainerMobile)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    flex: unset;
    width: 100%;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

export const RightAvatarContainer = styled(ContentContainer)`
  align-self: flex-end;
  cursor: pointer;
  overflow: hidden;
  align-items: center;
`;

export const Divider = styled.div`
  background-color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  height: 2rem;
  margin: auto 1rem;
  opacity: 10%;
  width: 1px;
`;

export const Link = styled(NavLink)`
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  margin: auto 0.75rem auto 0;
  padding: 5px 10px;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  text-decoration: none;
  overflow: hidden;
  white-space: nowrap;
  align-self: center;
  height: ${headerHeight};
  display: flex;
  justify-content: center;

  &.active {
    color: ${(props) => props.theme.colors.c800};
    font-weight: ${(props) => props.theme.fontWeight.semibold};
    border-bottom: 3px ${(props) => props.theme.colors.primary.c500} solid;
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
  }
`;

export const UserName = styled.span`
  color: ${(props: ThemeProp) => props.theme.colors.greyscale.c050};
  padding: 5px 10px;
  padding-left: 0;
  margin: auto;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 20rem;

  &:hover {
    color: ${(props: ThemeProp) => props.theme.colors.mainColor};
    text-decoration-line: underline;
  }

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const StyledAvatar = styled(Avatar)`
  margin: auto 0.5rem auto 0;
`;

export const UpgradePlanLink = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.black};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  color: ${(props: ThemeProp) => props.theme.colors.mainColor};
  cursor: pointer;
  margin-right: 2rem;

  &:hover {
    color: ${(props: ThemeProp) => props.theme.colors.mainColor};
    text-decoration-line: underline;
  }
`;

export const LinksContainer = styled.div`
  display: flex;
  flex: 1;
  margin-right: 1.75rem;
`;

export const LinkWrapper = styled.div`
  flex: 1;
  gap: 32px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  overflow-x: auto;
  align-content: flex-start;

  ::-webkit-scrollbar {
    display: none;
  }
`;

export const headerTitleFontStyles = (props: ThemeProp) => `
  font-weight: ${props.theme.fontWeight.medium};
  font-size: 1.0625rem;
  line-height: ${props.theme.lineHeight.m};
  color: ${props.theme.colors.neutrals.c900};
`;

interface TitleProps {
  $alignCenter?: boolean;
}

export const Title = styled.span<TitleProps>`
  ${(props: ThemeProp) => headerTitleFontStyles(props)}
  display: flex;
  align-items: center;
  margin: ${(props: TitleProps) => (props.$alignCenter ? 'auto' : '0')};

  font-size: ${(props: ThemeProp) => props.theme.fontSize.mlg};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.minMobile}) {
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
    font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.h2.fontSize};
    line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.h2.lineHeight};
  }
`;

export const GoBackChevron = styled(Chevron2Icon)`
  transform: rotate(90deg);
  & path {
    stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c200} !important;
    stroke-width: 1px;
  }
`;

export const ChevronContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 100%;
  padding-right: 0.75rem;
  cursor: pointer;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const Badge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: -2px;
  top: -2px;
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 50%;
  color: ${(props: ThemeProp) => props.theme.colors.white};
  background-color: ${(props) => props.theme.colors.system.error};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xxs};
`;

export const NotificationIconContainer = styled.div`
  display: flex;
  position: relative;
`;

export const StyledBellIcon = styled(BellIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.greyscale.c300};
`;

export const Logo = styled(VinCarLogoTypeColor)`
  margin: auto;
`;

export const BackButtonTabletWrapper = styled.div`
  display: none;
  height: 100%;
  margin-top: 0.125rem;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

export const BackButtonDesktopWrapper = styled.div`
  display: none;

  @media (min-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    display: flex;
  }
`;

export const StyledIconButton = styled(IconButton)<any>`
  width: 2rem;
  height: 2rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
  border-radius: 6px;
  background: ${(props: ThemeProp) => props.theme.colors.layout.surface};
  margin-right: 1rem;
  svg {
    path {
      stroke-width: 1.5px;
      color: ${(props: ThemeProp) => props.theme.colors.neutrals.c900};
    }
  }
`;

export const DesktopLogo = styled(VinCarLogoMark)`
  width: 2rem;
  height: 2rem;
`;

export const LogoSeparator = styled.div`
  width: 1px;
  height: 2rem;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
`;

export const HeaderCancelButton = styled(SecondaryButton)`
  align-self: center;
  height: 2rem;
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const MobileCloseButton = styled(IconButton)`
  display: none;
  position: absolute;
  right: 1rem;
  width: 1.5rem;
  height: 1.5rem;
  svg {
    stroke: ${(props) => props.theme.colors.greyscale.c100};
  }
  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    display: flex;
  }
`;
